import { useCallback, useEffect, useRef, useState } from "react";

import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import AudiotrackRoundedIcon from "@mui/icons-material/AudiotrackRounded";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import Typography from "@mui/material/Typography";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import { alpha } from "@mui/material/styles";

import pdfFileIcon from "../../../../assets/icons/file-pdf.png";
import { BadgeBase } from "../../../ui";

const mediaMessageTypes = new Set(["audio", "document", "image", "sticker", "video"]);

const mediaTypeConfig = {
  audio: {
    actionLabel: "Ouvir",
    icon: AudiotrackRoundedIcon,
    label: "Áudio",
  },
  document: {
    actionLabel: "Abrir",
    icon: DescriptionRoundedIcon,
    label: "Documento",
  },
  image: {
    actionLabel: "Abrir",
    icon: ImageRoundedIcon,
    label: "Imagem",
  },
  sticker: {
    actionLabel: "Abrir",
    icon: ImageRoundedIcon,
    label: "Figurinha",
  },
  video: {
    actionLabel: "Reproduzir",
    icon: VideocamRoundedIcon,
    label: "Vídeo",
  },
  file: {
    actionLabel: "Abrir",
    icon: AttachFileRoundedIcon,
    label: "Arquivo",
  },
};

const mediaStatusConfig = {
  available: { label: "Disponível", tone: "success" },
  failed: { label: "Falhou", tone: "error" },
  pending: { label: "Processando", tone: "info" },
  processing: { label: "Processando", tone: "info" },
};

function normalizeMediaToken(value) {
  return String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function getMediaTypeFromMimeType(mimeType) {
  const value = String(mimeType || "").trim().toLowerCase();
  if (value.startsWith("audio/")) return "audio";
  if (value.startsWith("image/")) return "image";
  if (value.startsWith("video/")) return "video";
  if (value.includes("pdf") || value.includes("document") || value.includes("spreadsheet")) {
    return "document";
  }
  return "";
}

export function getMessageMediaAsset(message) {
  const sourceAsset = message?.media_asset && typeof message.media_asset === "object"
    ? message.media_asset
    : {};
  const messageType = normalizeMediaToken(
    sourceAsset.message_type ||
      message?.message_type ||
      getMediaTypeFromMimeType(sourceAsset.mime_type || message?.media_mime_type),
  );
  const mediaAssetId = String(
    sourceAsset.media_asset_id ||
      message?.media_asset_id ||
      "",
  ).trim();
  const providerMediaId = String(
    sourceAsset.provider_media_id ||
      message?.provider_media_id ||
      message?.media_id ||
      "",
  ).trim();

  if (!mediaAssetId && !providerMediaId && !mediaMessageTypes.has(messageType)) {
    return null;
  }

  return {
    ...sourceAsset,
    caption: sourceAsset.caption || message?.media_caption,
    filename: sourceAsset.filename || message?.media_filename,
    media_asset_id: mediaAssetId,
    message_type: mediaMessageTypes.has(messageType) ? messageType : "file",
    mime_type: sourceAsset.mime_type || message?.media_mime_type,
    provider_media_id: providerMediaId,
    sha256: sourceAsset.sha256 || message?.media_sha256,
    status: normalizeMediaToken(
      sourceAsset.status ||
        message?.media_asset_status ||
        (sourceAsset.storage_key ? "available" : ""),
    ),
  };
}

export function hasMessageMedia(message) {
  return Boolean(getMessageMediaAsset(message));
}

export function getMediaPresentation(asset) {
  const type = normalizeMediaToken(asset?.message_type) || getMediaTypeFromMimeType(asset?.mime_type);
  return mediaTypeConfig[type] || mediaTypeConfig.file;
}

export function getMediaStatusPresentation(status) {
  const normalizedStatus = normalizeMediaToken(status);
  return mediaStatusConfig[normalizedStatus] || {
    label: normalizedStatus ? "Registrada" : "Aguardando",
    tone: "neutral",
  };
}

export function formatFileSize(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${Math.round(value / 1024 / 102.4) / 10} MB`;
}

function getMediaDisplayName(asset) {
  const presentation = getMediaPresentation(asset);
  return String(
    asset?.filename ||
      asset?.caption ||
      (asset?.message_type ? presentation.label : asset?.media_asset_id) ||
      presentation.label,
  ).trim();
}

function getMediaAccessErrorMessage(error) {
  if (error?.error === "media_not_available") return "Mídia ainda não está disponível.";
  if (error?.message) return error.message;
  return "Não foi possível carregar a mídia.";
}

function getMediaBubbleStyle(theme) {
  const isDark = theme.palette.mode === "dark";

  return {
    backgroundColor: isDark
      ? alpha(theme.palette.common.white, 0.06)
      : "#f1f5f9",
  };
}

function isPdfMedia(media) {
  const mimeType = String(media?.mime_type || "").trim().toLowerCase();
  const filename = String(media?.filename || "").trim().toLowerCase();
  return mimeType.includes("pdf") || filename.endsWith(".pdf");
}

export default function TimelineMediaAttachment({
  contactPointId,
  item,
  onLoadMediaAccess,
  reserveTopActionSpace = false,
  separated = false,
  timestamp = "",
}) {
  const asset = getMessageMediaAsset(item);
  const mediaAssetId = asset?.media_asset_id;
  const [access, setAccess] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const autoLoadKeyRef = useRef("");

  useEffect(() => {
    setAccess(null);
    setError("");
    setLoading(false);
    autoLoadKeyRef.current = "";
  }, [mediaAssetId]);

  const presentation = getMediaPresentation(asset);
  const MediaIcon = presentation.icon;
  const accessUrl = access?.access?.url || access?.url || "";
  const media = access?.media || asset || {};
  const status = normalizeMediaToken(media?.status || asset?.status);
  const statusPresentation = getMediaStatusPresentation(status);
  const name = getMediaDisplayName(media);
  const fileSize = formatFileSize(media?.size_bytes);
  const detail = [media?.mime_type, fileSize].filter(Boolean).join(" · ");
  const caption = String(media?.caption || "").trim();
  const canLoadAccess = Boolean(mediaAssetId && onLoadMediaAccess);
  const isPdf = isPdfMedia(media);
  const isAudio = presentation === mediaTypeConfig.audio;
  const isDocument = presentation === mediaTypeConfig.document || presentation === mediaTypeConfig.file;
  const isImage = presentation === mediaTypeConfig.image || presentation === mediaTypeConfig.sticker;
  const isFailed = status === "failed";
  const isPending = status === "pending" || status === "processing";
  const isVideo = presentation === mediaTypeConfig.video;
  const showStatus = isFailed || isPending;
  const rootWidth = isImage
    ? { xs: "100%", sm: presentation === mediaTypeConfig.sticker ? 256 : 312 }
    : { xs: "100%", sm: 346 };
  const accessStateLabel = loading
    ? "Carregando mídia"
    : !mediaAssetId
    ? "Sem id de mídia"
    : isPending
      ? "Aguardando download"
      : "";

  const handleLoadAccess = useCallback(async () => {
    if (!canLoadAccess || loading) return;

    setLoading(true);
    setError("");
    try {
      const result = await onLoadMediaAccess({
        contactPointId,
        expiresIn: 300,
        mediaAssetId,
      });
      const nextUrl = result?.access?.url || result?.url || "";
      const nextStatus = normalizeMediaToken(result?.media?.status || status);
      setAccess(result || null);
      if (!nextUrl && nextStatus !== "pending" && nextStatus !== "processing") {
        setError("Mídia ainda não está disponível.");
      }
    } catch (nextError) {
      setError(getMediaAccessErrorMessage(nextError));
    } finally {
      setLoading(false);
    }
  }, [canLoadAccess, contactPointId, loading, mediaAssetId, onLoadMediaAccess, status]);

  useEffect(() => {
    if (!canLoadAccess || accessUrl || error || isFailed || isPending || loading) return;
    if (autoLoadKeyRef.current === mediaAssetId) return;

    autoLoadKeyRef.current = mediaAssetId;
    handleLoadAccess();
  }, [accessUrl, canLoadAccess, error, handleLoadAccess, isFailed, isPending, loading, mediaAssetId]);

  if (!asset) return null;

  return (
    <Box
      sx={(theme) => {
        const bubbleStyle = getMediaBubbleStyle(theme);

        return {
          backgroundColor: bubbleStyle.backgroundColor,
          border: 0,
          borderRadius: "8px",
          color: theme.palette.text.primary,
          mt: separated ? 1 : 0,
          overflow: "hidden",
          pb: 0.5,
          pl: 1,
          pr: 0.5,
          pt: 1,
          width: rootWidth,
          maxWidth: "100%",
        };
      }}
    >
      {accessUrl && isAudio ? (
        <Box
          sx={(theme) => ({
            backgroundColor: theme.palette.background.paper,
            borderRadius: "8px",
            px: 1,
            py: 0.5,
            pr: reserveTopActionSpace ? 3 : 1,
          })}
        >
          <Box
            component="audio"
            controls
            preload="metadata"
            src={accessUrl}
            sx={{
              display: "block",
              height: 54,
              maxWidth: "100%",
              width: "100%",
            }}
          />
        </Box>
      ) : null}

      {accessUrl && isImage ? (
        <Box
          component="a"
          href={accessUrl}
          rel="noopener noreferrer"
          target="_blank"
          sx={{ display: "block", lineHeight: 0 }}
        >
          <Box
            component="img"
            alt={caption || name}
            src={accessUrl}
            sx={{
              backgroundColor: "background.paper",
              borderRadius: 0,
              display: "block",
              maxHeight: presentation === mediaTypeConfig.sticker ? 180 : 360,
              objectFit: "contain",
              width: "100%",
            }}
          />
        </Box>
      ) : null}

      {accessUrl && isVideo ? (
        <Box
          component="video"
          controls
          preload="metadata"
          src={accessUrl}
          sx={{
            backgroundColor: "common.black",
            borderRadius: "6px",
            display: "block",
            maxHeight: 340,
            width: "100%",
          }}
        />
      ) : null}

      {accessUrl && isDocument ? (
        <Box
          component="a"
          href={accessUrl}
          rel="noopener noreferrer"
          target="_blank"
          sx={(theme) => ({
            alignItems: "center",
            backgroundColor: theme.palette.background.paper,
            border: 0,
            borderRadius: "8px",
            color: theme.palette.text.primary,
            display: "flex",
            minHeight: 51,
            minWidth: 0,
            px: 1,
            py: 1,
            pr: reserveTopActionSpace ? 5 : 1,
            textDecoration: "none",
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
            },
          })}
        >
          {isPdf ? (
            <Box
              component="img"
              alt=""
              src={pdfFileIcon}
              sx={{
                flexShrink: 0,
                height: 35,
                objectFit: "contain",
                width: 30,
              }}
            />
          ) : (
            <Box
              sx={(theme) => ({
                alignItems: "center",
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                borderRadius: "6px",
                color: "primary.main",
                display: "inline-flex",
                flexShrink: 0,
                height: 34,
                justifyContent: "center",
                width: 34,
              })}
            >
              <MediaIcon sx={{ fontSize: 20 }} />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0, ml: 1 }}>
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: 12,
                fontWeight: 500,
                lineHeight: "20px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={name}
            >
              {name}
            </Typography>
            {fileSize || media?.mime_type ? (
              <Typography sx={{ color: "text.secondary", fontSize: 10, lineHeight: "15px" }}>
                {fileSize || media?.mime_type}
              </Typography>
            ) : null}
          </Box>
          <OpenInNewRoundedIcon sx={{ color: "text.secondary", flexShrink: 0, fontSize: 20 }} />
        </Box>
      ) : null}

      {caption && caption !== name && (accessUrl || !isDocument) ? (
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: 12,
            lineHeight: 1.45,
            mt: 1,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {caption}
        </Typography>
      ) : null}

      {!accessUrl ? (
        <>
          <Box
            sx={(theme) => ({
              alignItems: "center",
              backgroundColor: theme.palette.background.paper,
              borderRadius: "8px",
              display: "flex",
              minHeight: 51,
              minWidth: 0,
              px: 1,
              py: 1,
              pr: reserveTopActionSpace ? 5 : 1,
            })}
          >
            <Box
              sx={(theme) => ({
                alignItems: "center",
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                borderRadius: "8px",
                color: theme.palette.primary.main,
                display: "inline-flex",
                flexShrink: 0,
                height: 34,
                justifyContent: "center",
                width: 34,
              })}
            >
              {loading ? <CircularProgress color="inherit" size={17} /> : <MediaIcon sx={{ fontSize: 19 }} />}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, ml: 1 }}>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: "20px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={name}
              >
                {name}
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: 10,
                  lineHeight: "15px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {accessStateLabel || fileSize || detail || presentation.label}
              </Typography>
            </Box>

            {showStatus ? (
              <BadgeBase size="sm" tone={statusPresentation.tone}>
                {statusPresentation.label}
              </BadgeBase>
            ) : null}
          </Box>

          {error && canLoadAccess ? (
            <Button
              disabled={loading}
              onClick={handleLoadAccess}
              size="small"
              startIcon={<RefreshRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{ borderRadius: 1, mt: 0.75, textTransform: "none" }}
              variant="outlined"
            >
              Tentar novamente
            </Button>
          ) : null}
        </>
      ) : null}

      {error ? (
        <Typography sx={{ color: "error.main", fontSize: 11.5, lineHeight: 1.35, mt: 0.75 }}>
          {error}
        </Typography>
      ) : null}

      {timestamp ? (
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: 10,
            fontWeight: 500,
            lineHeight: "15px",
            mt: 1,
            textAlign: "right",
          }}
        >
          {timestamp}
        </Typography>
      ) : null}
    </Box>
  );
}
